import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { ProductContainer } from '../../state/product';
import { ApiProduct, ApiProductVariants } from '../../types';
import { Img } from '../Img/Img';
import { PriceTagSimple } from '../PriceTagSimple/PriceTagSimple';
import { DeliveryTime } from './DeliveryTime';
import { VariantDimensionOptions, VariantOption, variantOptions } from './variants';
import './VariantSelector.css';
import { caret_right, Icon } from '../Icon/Icon';

/**
 * One dropdown per dimension the product varies on (colour, width, ...), right under the name.
 * Closed, a row shows the product's own thumbnail and "Färg: Grön"; open, every value in the group
 * with the thumbnail, price and delivery status of the product it leads to. Values that exist
 * together with the product's other values lead to that product, the rest are shown disabled so
 * the customer sees the full range.
 */
export const VariantSelector = ({
	variants,
	groupProducts,
	currentId,
}: {
	variants: ApiProductVariants;
	groupProducts: ApiProduct[];
	currentId: number;
}) => {
	const { locale } = useConfig();
	const dimensions = variantOptions(variants, groupProducts, currentId, locale);
	if (dimensions.length === 0) return <></>;

	return (
		<div className="variant-selector">
			{dimensions.map((dimension) => (
				<VariantDropdown key={dimension.dimension} {...dimension} />
			))}
		</div>
	);
};

const VariantDropdown = ({ dimension, options }: VariantDimensionOptions) => {
	const t = useTranslation().product;
	const history = useHistory();
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	const header = useRef<HTMLButtonElement>(null);
	const optionButtons = useRef<(HTMLButtonElement | null)[]>([]);
	const selected = options.find((o) => o.selected);
	const dimensionName = (name: string) => (t.variantDimensions as Record<string, string>)[name] ?? name;
	const label = dimensionName(dimension);

	// a click or tap anywhere else closes the list, as does Escape. Touch and mouse events rather
	// than pointer events, which the Safari 12 the legacy build targets does not have
	useEffect(() => {
		if (!open) return;
		const onPress = (event: Event) => {
			if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', onPress);
		document.addEventListener('touchstart', onPress);
		return () => {
			document.removeEventListener('mousedown', onPress);
			document.removeEventListener('touchstart', onPress);
		};
	}, [open]);

	// the list opens on the shown product and the arrow keys walk the values, as a native select does;
	// the options are out of the Tab order so Tab leaves the dropdown instead of walking the list
	useEffect(() => {
		if (!open) return;
		const index = Math.max(
			0,
			options.findIndex((o) => o.selected)
		);
		optionButtons.current[index]?.focus();
	}, [open]);

	const focusOption = (index: number) => {
		const bounded = Math.min(options.length - 1, Math.max(0, index));
		optionButtons.current[bounded]?.focus();
	};

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		const focused = optionButtons.current.indexOf(document.activeElement as HTMLButtonElement);
		switch (event.key) {
			case 'Escape':
				setOpen(false);
				header.current?.focus();
				break;
			case 'ArrowDown':
				event.preventDefault();
				if (!open) setOpen(true);
				else focusOption(focused + 1);
				break;
			case 'ArrowUp':
				event.preventDefault();
				if (open) focusOption(focused - 1);
				break;
			case 'Home':
				if (open) {
					event.preventDefault();
					focusOption(0);
				}
				break;
			case 'End':
				if (open) {
					event.preventDefault();
					focusOption(options.length - 1);
				}
				break;
			default:
				// a typed character jumps to the next value starting with it
				if (open && event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
					const typed = event.key.toLowerCase();
					for (let step = 1; step <= options.length; step++) {
						const index = (focused + step) % options.length;
						if (options[index].label.toLowerCase().startsWith(typed)) {
							focusOption(index);
							break;
						}
					}
				}
		}
	};

	const choose = (option: VariantOption) => {
		if (!option.product) return;
		setOpen(false);
		if (option.selected) {
			header.current?.focus();
			return;
		}
		// counted in Plausible per dimension, to see how much the selector is used
		if (typeof window !== 'undefined' && window.plausible) {
			window.plausible('variantSwitched', { props: { dimension } });
		}
		history.push('/' + option.product.perma);
	};

	// tabbing out of the dropdown closes it too, not only a click elsewhere. On iOS a tap does not
	// focus the tapped button but does blur the focused option, which looked like leaving the
	// dropdown and closed the list before the tap's click could toggle or choose anything, so a
	// blur to nowhere during a tap inside the dropdown is left to the click handlers
	const tapping = useRef(false);
	const onPress = () => {
		tapping.current = true;
		window.setTimeout(() => (tapping.current = false), 1000);
	};
	const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
		if (tapping.current && !event.relatedTarget) return;
		if (root.current && !root.current.contains(event.relatedTarget as Node | null)) setOpen(false);
	};

	return (
		<div
			ref={root}
			className={`variant-dropdown${open ? ' open' : ''}`}
			onKeyDown={onKeyDown}
			onBlur={onBlur}
			onMouseDown={onPress}
			onTouchStart={onPress}
		>
			<button
				ref={header}
				type="button"
				className="variant-dropdown-header"
				aria-haspopup="listbox"
				aria-expanded={open}
				onClick={() => setOpen((prev) => !prev)}
			>
				<VariantThumbnail
					product={selected?.product ?? null}
					label={selected?.label ?? ''}
					swatch={selected?.swatch ?? null}
				/>
				<span className="variant-dropdown-text">
					<span className="variant-dropdown-label">{label}:</span>{' '}
					<strong className="variant-dropdown-value">{selected?.label ?? ''}</strong>
				</span>
				<Icon className="variant-dropdown-caret" size={16} shape={caret_right} />
			</button>
			{open && (
				<ul className="variant-dropdown-list" role="listbox" aria-label={label}>
					{options.map((option, index) => (
						<li
							key={option.value}
							role="none"
							className={`variant-dropdown-option${option.selected ? ' selected' : ''}${
								option.product ? '' : ' disabled'
							}`}
						>
							<button
								ref={(element) => (optionButtons.current[index] = element)}
								type="button"
								tabIndex={-1}
								role="option"
								aria-selected={option.selected}
								aria-disabled={!option.product}
								onClick={() => choose(option)}
							>
								<VariantThumbnail
									product={option.product}
									label={option.label}
									swatch={option.swatch}
								/>
								<span className="variant-dropdown-option-value">
									{option.label}
									{/* a value the shown product's combination does not come in leads to the
									    nearest one that does; say what else that changes */}
									{option.changes.length > 0 && (
										<span className="variant-dropdown-option-change">
											{option.changes
												.map((c) => `${dimensionName(c.dimension)}: ${c.label}`)
												.join(', ')}
										</span>
									)}
								</span>
								{option.product ? (
									<VariantOptionDetails product={option.product} />
								) : (
									<span className="variant-dropdown-option-missing">{t.variantNotAvailable}</span>
								)}
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

/* the colour itself when the value carries one, since the photo of a glass board looks the same in
   every colour; otherwise the product's own thumbnail, and its first letters when it has no picture */
const VariantThumbnail = ({
	product,
	label,
	swatch,
}: {
	product: ApiProduct | null;
	label: string;
	swatch: string | null;
}) => (
	<span className={`variant-dropdown-thumbnail${swatch ? ' swatch' : ''}`}>
		{swatch ? (
			<span className="variant-dropdown-swatch" style={{ backgroundColor: swatch }} title={label} />
		) : product && product.image ? (
			<Img src={product.image} alt={label} width={50} height={50} imgWidth={40} imgHeight={40} />
		) : (
			<span className="variant-dropdown-thumbnail-text">{label.slice(0, 3)}</span>
		)}
	</span>
);

/** the delivery time with its icon as the product cards show it, and the price on the right */
const VariantOptionDetails = ({ product }: { product: ApiProduct }) => {
	const { getFinalPrice } = ProductContainer.useContainer();
	return (
		<>
			<span className="variant-dropdown-option-delivery">
				<DeliveryTime
					deliveryClass={product.deliveryClass}
					deliveryNotice={product.deliveryNotice}
					textLength="short-one-line"
				/>
			</span>
			<span className={`variant-dropdown-option-price${product.campaignPrice > 0 ? ' campaign' : ''}`}>
				<PriceTagSimple
					price={getFinalPrice(product.id, product.price, 1, product.campaignPrice)}
					isCampaign={product.campaignPrice > 0}
					vatRate={product.vatRate}
					onlyText
				/>
			</span>
		</>
	);
};
